import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Detail, FlexColumn, Pill, PillVariant, Text} from '@forecast-it/design-system';
import novaMark from '../../../../images/nova/nova-mark.svg';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NovaDryRunTurn} from '../state/useNovaDryRun';
import {NovaModel, describeNovaModel} from '../shared/novaModelList';
import {NovaGroundingBreakdownPanel} from './NovaGroundingBreakdown';

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

// The provenance line sits outside the card, in small muted type: it describes the run rather than
// being part of what the agent said, and keeping it out of the tinted panel preserves that difference.
const MetaLine = styled.div`
	letter-spacing: 0.02em;
`;

// The tinted panel is the agent speaking. Everything above it in the tab is the author's own input, so
// the change of background is doing real work — it marks where authored text stops and generated text
// starts.
const Card = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 14px 16px;
	background: ${NovaStudioColor.novaSurface};
	border: 1px solid ${NovaStudioColor.novaSurfaceBorder};
	border-radius: 10px;
`;

// The mark carries its own rounded gradient tile, so it needs no wrapper to sit on — only a fixed
// square footprint so a row of pills beside it cannot squash it.
const NovaMark = styled.img`
	width: 28px;
	height: 28px;
	flex-shrink: 0;
`;

const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

// Pushes the timestamp to the far end of the header, whatever the pills in front of it add up to.
const HeaderSpacer = styled.div`
	margin-left: auto;
`;

// The answer is free text from a model; preserving the newlines it wrote is the whole of the
// formatting it needs here, and pre-wrap does that without introducing a renderer that would also
// have to be trusted with the content.
const Output = styled.div`
	white-space: pre-wrap;
`;

const Question = styled.div`
	white-space: pre-wrap;
`;

const FooterRow = styled.div`
	display: flex;
	justify-content: flex-end;
`;

const SEGMENT_SEPARATOR = ' · ';

interface Props {
	agentName: string;
	taskTitle: string;
	turn: NovaDryRunTurn;
	// Used only to turn the run's raw model id into a display name; an id the list does not carry
	// (a since-retired model) is shown as-is.
	models: NovaModel[];
	dataCy: string;
}

// One turn's answer, presented the way a real run will appear — who answered, against which task, and
// when — so an author reviewing the output is reading the same thing a customer eventually would.
//
// The dry-run marking is not decoration. This is the one place an author could mistake a test for a
// live run, so the line above every card says both that it was a dry run and that nothing was posted.
export function NovaOutputCard({agentName, taskTitle, turn, models, dataCy}: Props): JSX.Element {
	const {formatMessage, formatDate} = useIntl();

	// Built by joining present segments rather than from a single interpolated message so a missing
	// task title collapses cleanly instead of leaving a dangling separator.
	const modelName = describeNovaModel(models, turn.modelId);
	const metaSegments = [
		formatMessage({id: 'nova.test.metaDryRun'}),
		formatMessage({id: 'nova.test.metaNothingPosted'}),
		agentName,
		taskTitle,
		modelName === '' ? '' : formatMessage({id: 'nova.test.metaModel'}, {model: modelName}),
	].filter(segment => segment !== '');

	return (
		<Wrapper data-testid={dataCy} data-cy={dataCy}>
			<MetaLine data-testid={`${dataCy}-meta`} data-cy={`${dataCy}-meta`}>
				<Detail>{metaSegments.join(SEGMENT_SEPARATOR)}</Detail>
			</MetaLine>
			<Card>
				<HeaderRow>
					{/* Decorative: the name "Nova" is spelled out immediately after it. */}
					<NovaMark src={novaMark} alt="" data-testid={`${dataCy}-nova-mark`} data-cy={`${dataCy}-nova-mark`} />
					<Text>Nova</Text>
					<Pill variant={PillVariant.HIGHLIGHT}>{agentName}</Pill>
					<Pill variant={PillVariant.INFORMATION}>{formatMessage({id: 'nova.test.aiAuthored'})}</Pill>
					{taskTitle === '' ? null : <Pill variant={PillVariant.NEUTRAL}>{taskTitle}</Pill>}
					<HeaderSpacer />
					<Detail data-testid={`${dataCy}-timestamp`} data-cy={`${dataCy}-timestamp`}>
						{formatDate(turn.ranAt, {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							hour: '2-digit',
							minute: '2-digit',
						})}
					</Detail>
				</HeaderRow>
				{turn.message === null ? null : (
					<Question data-testid={`${dataCy}-question`} data-cy={`${dataCy}-question`}>
						<Detail>{turn.message}</Detail>
					</Question>
				)}
				<Output data-testid={`${dataCy}-output`} data-cy={`${dataCy}-output`}>
					<Text>{turn.output}</Text>
				</Output>
				<FooterRow>
					<Detail>{formatMessage({id: 'nova.test.cardSourceNote'})}</Detail>
				</FooterRow>
			</Card>
			{/*
			 * No panel at all for a run that recorded no account, rather than an empty one: four empty
			 * lists under "Grounded in 0 sources" would state that this run read nothing, when what is
			 * actually true is that we cannot say what it read.
			 */}
			{turn.grounding === null ? null : (
				<FlexColumn>
					<NovaGroundingBreakdownPanel breakdown={turn.grounding} dataCy={`${dataCy}-grounding`} />
				</FlexColumn>
			)}
		</Wrapper>
	);
}
